import { useCallback, useEffect, useState } from "react";
import { FileSpreadsheet, ArrowUpRight, Download, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { api, API } from "../api";
import { PageHead, Panel, LoadingBlock } from "../shell";
import { money } from "../lib/format";
import { can } from "../lib/permissions";
import { ImportSellerMapping } from "./import/ImportSellerMapping";

export default function ImportPage({ user }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [mapping, setMapping] = useState({});
  const [kind, setKind] = useState("policies");
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [datasetState, setDatasetState] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);

  const loadHistory = useCallback(() => can(user,"imports.history") ? api.get("/import/history").then((r) => setHistory(r.data.items)) : Promise.resolve(), [user]);
  const loadState = () => api.get("/dataset/state").then((r) => setDatasetState(r.data));
  useEffect(() => { loadHistory(); loadState(); }, [loadHistory]);
  useEffect(() => {
    if (!activity) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [activity]);
  const hasPendingBatch = history.some((batch) => ["ACTIVATING", "CLASSIFYING", "REPAIRING_STAGING"].includes(batch.status));
  const pendingActivation = history.find((batch) => batch.status === "ACTIVATING");
  useEffect(() => {
    if (!hasPendingBatch) return undefined;
    const timer = window.setInterval(() => loadHistory().catch(() => {}), 2000);
    return () => window.clearInterval(timer);
  }, [hasPendingBatch, loadHistory]);
  const beginActivity = (label) => {
    setBusy(true); setClock(Date.now());
    setActivity({ label, startedAt: Date.now(), progress: null });
  };
  const endActivity = () => { setBusy(false); setActivity(null); };

  const openReconciliation = async (batchId) => {
    setReconciliation(null);
    try { const r = await api.get(`/dataset/reconciliation/${batchId}`); setReconciliation(r.data); }
    catch (e) { alert(e.response?.data?.detail || "Mutabakat alınamadı"); }
  };

  const uploadPreview = async () => {
    if (!file) return;
    beginActivity("Dosya sunucuya gönderiliyor"); setResult(null); setPreview(null); setSheet(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: ({ loaded, total }) => setActivity((current) => current && ({
          ...current,
          label: total && loaded >= total ? "Dosya yüklendi; sayfalar ve kolonlar inceleniyor" : "Dosya sunucuya gönderiliyor",
          progress: total && loaded < total ? Math.round(loaded / total * 100) : null,
        })),
      });
      setPreview(r.data);
      if (r.data.sheets.length) {
        const first = r.data.sheets[0];
        setSheet(first.name); setMapping(first.auto_mapping || {});
      }
    } catch (e) { alert(e.response?.data?.detail || "Ön izleme oluşturulamadı"); }
    finally { endActivity(); }
  };

  const doCommit = async () => {
    if (!preview || !sheet) return;
    if (!window.confirm(`${sheet} sayfasındaki verileri sisteme almak istediğinize emin misiniz?`)) return;
    beginActivity("Satırlar doğrulanıyor ve aktarım hazırlanıyor");
    try {
      const r = await api.post("/import/commit", { preview_id: preview.preview_id, sheet, kind, mapping });
      setResult(r.data); setPreview(null); setSheet(null); setMapping({});
      loadHistory(); loadState();
    } catch (e) { alert(e.response?.data?.detail || "İçe aktarım başarısız"); }
    finally { endActivity(); }
  };

  const repairStaging = async (batchId) => {
    if (!window.confirm("Bu taslağın satıcı bağlantıları güncel eşleştirmelerle kontrol edilecek. Tutarlar korunacak; aktivasyon ayrıca onaylanacak. Devam edilsin mi?")) return;
    beginActivity("Taslak satıcı bağlantıları kontrol ediliyor");
    try {
      const response = await api.post(`/import/${batchId}/staging/repair`);
      setResult(response.data); setReconciliation(null);
      await loadHistory();
    } catch (error) { alert(error.response?.data?.detail || "Taslak bağlantıları yenilenemedi; aktarım geçmişinden durumunu kontrol edin"); }
    finally { endActivity(); }
  };

  const activateProduction = async (batchId) => {
    beginActivity("Aktivasyon öncesi mutabakat kontrol ediliyor");
    try {
      const r = await api.get(`/dataset/reconciliation/${batchId}`);
      setReconciliation(r.data);
      if (!r.data.activation_allowed) {
        alert("Aktivasyon engellendi:\n\n" + r.data.activation_blockers.join("\n"));
        return;
      }
      if (!window.confirm(`MUTABAKAT ONAYI\n\nExcel toplamları veritabanı ile eşleşiyor:\n· Net prim delta: ${r.data.diffs.net_premium.delta}\n· Komisyon delta: ${r.data.diffs.commission.delta}\n· Satır sayısı delta: ${r.data.diffs.production_rows.delta}\n\nGerçek veriyi aktif etmek istediğinize emin misiniz?`)) return;
      setActivity((current) => ({ ...current, label: "Veriler aktif ediliyor ve raporlar hazırlanıyor" }));
      await api.post("/dataset/activate-production", { batch_id: batchId, confirm_reconciliation: true });
      await loadHistory();
      alert("Aktivasyon arka planda başlatıldı. İlerleme bu sayfada güncellenecek.");
    } catch (e) {
      loadHistory().catch(() => {});
      alert(e.response?.data?.detail || "İşlem sonucu alınamadı; sunucuda devam ediyor olabilir. Yeniden başlatmadan önce aktarım geçmişindeki durumu kontrol edin.");
    }
    finally { endActivity(); }
  };
  const revertDemo = async () => {
    if (!window.confirm("Demo veriye dönmek istediğinize emin misiniz?")) return;
    try { await api.post("/dataset/revert-demo"); await loadState(); alert("Demo veri aktif edildi"); }
    catch (e) { alert(e.response?.data?.detail || "Değişiklik yapılamadı"); }
  };
  const discardDraft = async (batch) => {
    const name = batch.filename || "Bu aktarım taslağı";
    if (!window.confirm(name + " silinsin mi? Eşleştirme bekleyen taslağın önizleme dosyası da silinir.")) return;
    try { await api.delete("/import/" + batch.id); await loadHistory(); }
    catch (e) { alert(e.response?.data?.detail || "Taslak silinemedi"); }
  };

  const sheetInfo = preview?.sheets.find((s) => s.name === sheet);
  const fields = preview?.field_spec ? Object.keys(preview.field_spec) : [];
  const canImport = can(user, "imports.manage");
  const canMap = can(user, "imports.mapping.manage");

  return (
    <main className="content">
      <PageHead eyebrow="ADMIN · IMPORT LAYER" title="Veri Aktarımı"
        desc="Excel dosyalarını (.xlsx / .xlsb) güvenli bir ön izleme ve eşleştirme akışıyla sisteme alın." />

      {pendingActivation && <div className="import-activity" role="status" aria-live="polite" data-testid="activation-progress">
        <strong>Üretim aktivasyonu sürüyor · {pendingActivation.filename}</strong>
        <span>{pendingActivation.activation_progress?.message || "Aktarım işleniyor; bu sayfayı yenileseniz de işlem devam eder."}</span>
        {typeof pendingActivation.activation_progress?.progress === "number" && <progress aria-label="Üretim aktivasyonu ilerlemesi" max="100" value={pendingActivation.activation_progress.progress} />}
      </div>}
      {activity && <div className="import-activity" role="status" aria-live="polite" data-testid="import-activity">
        <strong>{activity.label}</strong>
        <span>{Math.max(0, Math.floor((clock - activity.startedAt) / 1000))} sn · İşlem sonucu bekleniyor.</span>
        {activity.progress === null ? <progress aria-label={activity.label}/> : <>
          <progress aria-label="Dosya gönderme ilerlemesi" max="100" value={activity.progress}/><span>Dosya gönderimi %{activity.progress}</span>
        </>}
      </div>}

      <Panel title="Aktif veri kaynağı" sub="Sistem raporları aşağıdaki veri setini gösteriyor"
        actions={canImport && datasetState?.state?.active === "PRODUCTION" && datasetState?.can_revert_demo && (
          <button data-testid="revert-demo-btn" className="outline-button" onClick={revertDemo}>Demo'ya dön</button>
        )}>
        <div className="dataset-strip">
          <div className={"dataset-state " + (datasetState?.state?.active === "PRODUCTION" ? "prod" : "demo")}>
            <ShieldAlert size={16}/> {datasetState?.state?.active === "PRODUCTION" ? "ÜRETİM VERİSİ" : "DEMO VERİ"}
          </div>
          <div><span>Demo kayıt</span><strong>{new Intl.NumberFormat("tr-TR").format(datasetState?.counts?.demo_records || 0)}</strong></div>
          <div><span>Excel kayıt</span><strong>{new Intl.NumberFormat("tr-TR").format(datasetState?.counts?.excel_records || 0)}</strong></div>
          <div><span>Aktif eden</span><strong>{datasetState?.state?.activated_by || "system"}</strong></div>
        </div>
      </Panel>

      {canImport && <section className="import-layout">
        <Panel title="Dosya yükle" sub=".xlsx ve .xlsb dosyaları desteklenir">
          <label data-testid="excel-file-input" className="file-drop">
            <input type="file" accept=".xlsx,.xlsb" onChange={(e) => setFile(e.target.files[0])}/>
            <FileSpreadsheet size={26}/>
            <span>{file ? file.name : "Dosya seçin veya buraya sürükleyin"}</span>
            <small>{file ? `${(file.size/1024/1024).toFixed(2)} MB` : "Maks 200 MB"}</small>
          </label>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", flex: 1 }}>Kayıt tipi
              <select data-testid="import-kind-select" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="policies">Üretim (poliçe hareketleri)</option>
                <option value="targets">Hedefler</option>
              </select>
            </label>
          </div>
          <button data-testid="excel-upload-button" className="primary-button" onClick={uploadPreview}
            disabled={!file || busy} style={{ marginTop: 12, width: "100%" }}>
            {busy ? "İşleniyor..." : "Ön izleme oluştur"} <ArrowUpRight size={16}/>
          </button>
        </Panel>

        <Panel title="Aktarım akışı" sub="Adım adım kontrol">
          {["Dosya seçimi","Sayfa ve kolon eşleştirme","Ön izleme ve doğrulama","İçe aktarım ve mutabakat","Üretim verisi aktivasyonu"].map((x, i) => (
            <div key={x} className={"step " + (preview ? (i <= 1 ? "done" : "") : (i === 0 && file ? "done" : ""))}>
              <span>0{i+1}</span>
              <div><strong>{x}</strong>
                <small>{i===0?"Yüklenen dosya":i===1?"Kolon eşleştirme":i===2?"Hatalı satırlar":i===3?"Toplam mutabakat":"Admin onayı"}</small>
              </div>
            </div>
          ))}
        </Panel>
      </section>}

      {canImport && preview && (
        <Panel title={`Kolon eşleştirme · ${preview.filename}`}
          sub={`Sayfa: ${sheet || "—"} · ${preview.engine.toUpperCase()}`}
          actions={<button data-testid="import-commit-button" className="primary-button" onClick={doCommit} disabled={busy}>{busy ? "Aktarılıyor..." : "İçe aktar"} <ArrowUpRight size={15}/></button>}>
          <div className="advanced-filters">
            <label>Sayfa<select data-testid="import-sheet-select" value={sheet || ""} onChange={(e) => {
              setSheet(e.target.value);
              const sh = preview.sheets.find((s) => s.name === e.target.value);
              if (sh) setMapping(sh.auto_mapping || {});
            }}>
              {preview.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select></label>
          </div>
          <div className="table-panel" style={{ maxHeight: 300, overflow: "auto" }}>
            <table data-testid="mapping-table">
              <thead><tr><th>Sistem alanı</th><th>Excel kolonu</th><th>Örnek değer</th></tr></thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f}>
                    <td><strong>{f}</strong> <span className="muted" style={{ fontSize: 10 }}>{(preview.field_spec[f] || []).slice(0, 3).join(" / ")}</span></td>
                    <td>
                      <select data-testid={`mapping-${f}`} value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}>
                        <option value="">— Eşleştirme yok —</option>
                        {sheetInfo?.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </td>
                    <td className="muted">{mapping[f] && sheetInfo?.sample_rows[0]?.[mapping[f]] !== undefined ? String(sheetInfo.sample_rows[0][mapping[f]]) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sheetInfo && (
            <div style={{ marginTop: 12 }}>
              <h4 style={{ fontSize: 12, margin: "10px 0" }}>Örnek satırlar ({sheetInfo.sample_rows.length})</h4>
              <div className="table-panel" style={{ maxHeight: 220, overflow: "auto" }}>
                <table><thead><tr>{sheetInfo.headers.slice(0, 12).map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>{sheetInfo.sample_rows.map((r, i) => (
                    <tr key={i}>{sheetInfo.headers.slice(0, 12).map((h) => <td key={h}>{String(r[h] ?? "")}</td>)}</tr>
                  ))}</tbody></table>
              </div>
            </div>
          )}
        </Panel>
      )}

      {result && (
        <Panel title="İçe aktarım sonucu" sub={result.filename}>
          <div className="totals-strip">
            <div><span>Toplam satır</span><strong data-testid="result-count">{new Intl.NumberFormat("tr-TR").format(result.record_count)}</strong></div>
            <div><span>Üretim eklenen</span><strong className="positive">{new Intl.NumberFormat("tr-TR").format(result.inserted_count)}</strong></div>
            <div><span>Güncellenen</span><strong>{result.updated_count}</strong></div>
            <div><span>Duplicate</span><strong>{result.duplicate_count}</strong></div>
            <div><span>Hedef satırı</span><strong style={{color:"#c3943f"}}>{new Intl.NumberFormat("tr-TR").format(result.target_inserted || 0)}</strong></div>
            <div><span>Boş satır</span><strong>{new Intl.NumberFormat("tr-TR").format(result.empty_rows || 0)}</strong></div>
            <div><span>Reddedilen</span><strong className="negative">{result.rejected_count}</strong></div>
            <div><span>Eşleşmemiş satıcı</span><strong className={result.unmapped_seller_count > 0 ? "negative" : "positive"}>{result.unmapped_seller_count || 0}</strong></div>
          </div>
          {result.mapping_required && <div className="warn-banner" data-testid="import-draft-status"><AlertTriangle size={15}/> Dosya taslakta; organizasyon eşleştirmeleri çözülmeden aktif edilemez.</div>}
          {result.reconciliation && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 12, margin: "6px 0 10px" }}>Mutabakat özeti</h4>
              <div className="totals-strip">
                <div><span>Net prim</span><strong>{money(result.reconciliation.net_premium, true)}</strong></div>
                <div><span>Komisyon</span><strong>{money(result.reconciliation.commission, true)}</strong></div>
                <div><span>Performans Hedef</span><strong>{money(result.reconciliation.target_total, true)}</strong></div>
              </div>
            </div>
          )}
          {result.classification_pipeline && <div className="import-classification-result" data-testid="import-classification-result">
            <CheckCircle2 size={16}/><div><strong>Sınıflandırma otomatik güncellendi</strong><span>{new Intl.NumberFormat("tr-TR").format(result.classification_pipeline.materialized_row_count)} hareket · {new Intl.NumberFormat("tr-TR").format(result.classification_pipeline.impacted_period_count)} etkilenen dönem</span></div>
          </div>}
          {result.staging_repair && <div className="import-classification-result" data-testid="staging-repair-result">
            <CheckCircle2 size={16}/><span>Taslak bağlantıları kontrol edildi · {result.staging_repair.checked_rows} satır kontrol edildi, {result.staging_repair.updated_rows} satır güncellendi. Aktivasyon için mutabakatı kontrol edin.</span>
          </div>}
          {!result.mapping_required && <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
            {result.rejected_count > 0 && can(user,"imports.error_export") && (
              <a data-testid="download-errors-btn" className="outline-button" href={`${API}/import/errors/${result.id}/export`} target="_blank" rel="noreferrer">
                <Download size={14}/> Hatalı kayıtları indir
              </a>
            )}
            <button data-testid="reconciliation-btn" className="outline-button" onClick={() => openReconciliation(result.id)}>
              Mutabakat raporu
            </button>
            {result.status === "READY_TO_ACTIVATE" && canMap && <button data-testid="repair-staging-btn" className="outline-button" disabled={busy} onClick={() => repairStaging(result.id)}>Taslak bağlantılarını yenile</button>}
            {canImport && <button data-testid="activate-batch-btn" className="primary-button" disabled={busy} onClick={() => activateProduction(result.id)}>
              <CheckCircle2 size={14}/> Gerçek veriyi aktif et
            </button>}
          </div>}
          {result.mapping_required && canMap && <ImportSellerMapping batchId={result.id} onComplete={(completed) => {
            setResult(completed); loadHistory(); loadState();
          }}/>} 
        </Panel>
      )}

      {reconciliation && (
        <Panel title="Mutabakat Raporu" sub={reconciliation.import.filename}
          actions={<span className={"pill " + (reconciliation.activation_allowed ? "good" : "critical")}>
            {reconciliation.activation_allowed ? "AKTİVASYON ONAYLANABILIR" : "AKTİVASYON ENGELLENDİ"}
          </span>}>
          {reconciliation.activation_blockers.length > 0 && (
            <div className="warn-banner" style={{ marginBottom: 12 }} data-testid="reconciliation-blockers">
              {reconciliation.activation_blockers.map((b, i) => <div key={i}>· {b}</div>)}
            </div>
          )}
          <div className="table-panel"><table data-testid="reconciliation-table">
            <thead><tr><th>Metrik</th><th style={{textAlign:"right"}}>Excel</th><th style={{textAlign:"right"}}>Veritabanı</th><th style={{textAlign:"right"}}>Delta</th></tr></thead>
            <tbody>
              {Object.entries(reconciliation.diffs).map(([k, v]) => (
                <tr key={k}><td><strong>{k}</strong></td>
                  <td style={{textAlign:"right"}}>{typeof v.workbook === "number" ? new Intl.NumberFormat("tr-TR").format(v.workbook) : v.workbook}</td>
                  <td style={{textAlign:"right"}}>{typeof v.database === "number" ? new Intl.NumberFormat("tr-TR").format(v.database) : v.database}</td>
                  <td style={{textAlign:"right"}} className={Math.abs(v.delta) < 1 ? "positive" : "negative"}>{new Intl.NumberFormat("tr-TR").format(v.delta)}</td></tr>
              ))}
            </tbody>
          </table></div>
        </Panel>
      )}

      {can(user,"imports.history")&&<Panel title="İçe aktarım geçmişi" sub="Son 50 içe aktarım">
        <div className="table-panel"><table className="import-history-table" data-testid="import-history-table">
          <thead><tr><th>Tarih</th><th>Kullanıcı</th><th>Dosya</th><th>Sayfa</th><th>Tip</th><th>Durum</th>
            <th style={{textAlign:"right"}}>Eklendi</th><th style={{textAlign:"right"}}>Güncellendi</th>
            <th style={{textAlign:"right"}}>Reddedildi</th><th>Aksiyon</th></tr></thead>
          <tbody>{history.map((h) => (
            <tr key={h.id}><td data-label="Tarih">{new Date(h.import_date).toLocaleString("tr-TR")}</td>
              <td data-label="Kullanıcı">{h.imported_by}</td><td data-label="Dosya">{h.filename}</td><td data-label="Sayfa">{h.sheet}</td><td data-label="Tip">{h.kind}</td><td data-label="Durum" data-testid={`import-status-${h.id}`}>{({ MAPPING_REQUIRED: "Eşleştirme bekliyor", READY_TO_ACTIVATE: "Aktivasyon bekliyor", ACTIVATING: "Aktivasyon sürüyor", REPAIRING_STAGING: "Taslak bağlantıları yenileniyor", CLASSIFYING: "Sınıflandırma sürüyor", ACTIVATION_FAILED: "Aktivasyon başarısız · yeniden denenebilir", COMPLETED: "Tamamlandı" })[h.status] || h.status}</td>
              <td data-label="Eklenen" style={{textAlign:"right"}}>{h.inserted_count}</td>
              <td data-label="Güncellenen" style={{textAlign:"right"}}>{h.updated_count}</td>
              <td data-label="Reddedilen" style={{textAlign:"right"}}>{h.rejected_count}</td>
              <td data-label="İşlem" className="import-history-actions">
                <button data-testid={`reconciliation-${h.id}`} className="outline-button" onClick={() => openReconciliation(h.id)}>Mutabakat</button>
                {h.status === "MAPPING_REQUIRED" && canMap && <>
                <button data-testid={"mapping-" + h.id} className="outline-button" onClick={() => { setResult(null); setReconciliation(null); }}>Eşleştir</button>
                <button data-testid={"delete-draft-" + h.id} className="outline-button" disabled={busy} onClick={() => discardDraft(h)}>Taslağı sil</button>
              </>}
                {["READY_TO_ACTIVATE", "REPAIRING_STAGING"].includes(h.status) && canMap && <button data-testid={`repair-staging-${h.id}`} className="outline-button" disabled={busy} onClick={() => repairStaging(h.id)}>Taslak bağlantılarını yenile</button>}
                {["READY_TO_ACTIVATE", "ACTIVATION_FAILED"].includes(h.status) && canImport && <button data-testid={"activate-" + h.id} className="primary-button" disabled={busy || Boolean(pendingActivation)} onClick={() => activateProduction(h.id)}>{h.status === "ACTIVATION_FAILED" ? "Yeniden dene" : "Aktif et"}</button>}
              </td></tr>
          ))}</tbody>
        </table></div>
        {history.length === 0 && <div className="empty-state">Henüz içe aktarım yok.</div>}
      </Panel>}
    </main>
  );
}
